// Invitations after publish (Lane 1331, Phase 2 slice 2A). Per supplier: where
// it stands, each contact's link and email state, and the actions. The server
// never returns a link here; a new link comes back once, only while email is
// off, from the action that made it.
import React, { useCallback, useEffect, useState } from 'react';
import { SupplierPicker } from './ScopeBuilder';
import { STALE_MESSAGE } from './sourcingApi';
import InvitationLinks, { DELIVERY_TEXT } from './InvitationLinks';
import { formatDueAt } from '../sourcing-portal/portalView';

const SUPPLIER_STATUS_TEXT = { invited: 'Invited', declined: 'Declined', removed: 'Removed' };
const LINK_STATE_TEXT = {
  not_accepted: 'Link not used yet', accepted: 'Joined', expired: 'Link expired', revoked: 'Access removed',
};

// The newest invitation per email (the server lists them newest first).
function contactsOf(supplier) {
  const seen = new Map();
  (supplier.invitations || []).forEach((inv) => { if (!seen.has(inv.email)) seen.set(inv.email, inv); });
  return [...seen.values()];
}

function Times({ s }) {
  const rows = [
    ['Invited', s.invited_at], ['Opened', s.viewed_at], ['First quote', s.first_submitted_at],
    ['Latest quote', s.last_submitted_at], ['Declined', s.declined_at], ['Removed', s.removed_at],
  ].filter(([, v]) => v);
  if (rows.length === 0) return null;
  return (
    <div className="srce-times">
      {rows.map(([k, v]) => <span key={k}><span className="srce-k">{k}</span>{formatDueAt(v)}</span>)}
      {s.decline_reason && <span><span className="srce-k">Reason</span>{s.decline_reason}</span>}
    </div>
  );
}

function ContactForm({ busy, onSubmit, onCancel, submitLabel }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  return (
    <div className="srce-contact">
      <input className="srce-input" type="email" value={email} maxLength={255} placeholder="Email" aria-label="Email"
        onChange={(e) => setEmail(e.target.value)} />
      <input className="srce-input" value={name} maxLength={255} placeholder="Name (optional)" aria-label="Name"
        onChange={(e) => setName(e.target.value)} />
      <button type="button" className="srce-btn srce-btn--primary" disabled={busy || !email.trim()}
        onClick={() => onSubmit(email.trim(), name.trim())}>{submitLabel}</button>
      <button type="button" className="ll-clr" onClick={onCancel}>Cancel</button>
    </div>
  );
}

function SupplierBlock({ s, editable, busy, act }) {
  const [adding, setAdding] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const removed = s.status === 'removed';
  const canAct = editable && !removed;
  return (
    <div className={`srce-invsup${removed ? ' srce-tr--out' : ''}`}>
      <div className="srce-actions">
        <span className="srce-strong">{s.supplier_name}</span>
        <span className={`srce-status srce-status--${s.status === 'invited' ? 'open' : s.status === 'removed' ? 'cancelled' : 'draft'}`}>
          {SUPPLIER_STATUS_TEXT[s.status] || s.status}
        </span>
        {canAct && !confirmRemove && (
          <span className="srce-push srce-row">
            <button type="button" className="ll-clr" onClick={() => setAdding((v) => !v)}>Add contact</button>
            <button type="button" className="ll-clr" onClick={() => setConfirmRemove(true)}>Remove supplier</button>
          </span>
        )}
        {canAct && confirmRemove && (
          <span className="srce-push srce-row">
            <span className="srce-crit">Remove {s.supplier_name}? Their access stops now and their offers stop counting.</span>
            <button type="button" className="srce-btn srce-btn--primary" disabled={busy}
              onClick={async () => { setConfirmRemove(false); await act('revoke_supplier', { supplier_id: s.supplier_id }); }}>
              Yes, remove
            </button>
            <button type="button" className="ll-clr" onClick={() => setConfirmRemove(false)}>Keep</button>
          </span>
        )}
      </div>
      <Times s={s} />
      {contactsOf(s).length === 0 && <div className="srce-mut">No contacts.</div>}
      {contactsOf(s).map((c) => (
        <div key={c.invitation_id} className="srce-invrow">
          <span>
            {c.contact_name && <span className="srce-strong">{c.contact_name} </span>}
            <span>{c.email}</span>
          </span>
          <span>{LINK_STATE_TEXT[c.state] || c.state}
            {c.state === 'not_accepted' && c.expires_at ? `, expires ${formatDueAt(c.expires_at)}` : ''}
            {c.state === 'accepted' && c.accepted_at ? ` ${formatDueAt(c.accepted_at)}` : ''}
          </span>
          <span className="srce-mut">{c.delivery ? DELIVERY_TEXT[c.delivery] || c.delivery : ''}</span>
          <span className="srce-row srce-r">
            {canAct && ['not_accepted', 'expired'].includes(c.state) && (
              <button type="button" className="ll-clr" disabled={busy}
                onClick={() => act('reissue_invitation', { supplier_id: s.supplier_id, email: c.email })}>Reissue link</button>
            )}
            {canAct && c.state !== 'revoked' && (
              <button type="button" className="ll-clr" disabled={busy}
                onClick={() => act('revoke_invitation', { supplier_id: s.supplier_id, email: c.email })}>Revoke</button>
            )}
          </span>
        </div>
      ))}
      {canAct && adding && (
        <ContactForm busy={busy} submitLabel="Invite this person" onCancel={() => setAdding(false)}
          onSubmit={async (email, name) => {
            if (await act('invite_contact', { supplier_id: s.supplier_id, email, ...(name ? { name } : {}) })) setAdding(false);
          }} />
      )}
    </div>
  );
}

export default function InvitationsTab({ api, eventId, summary, onChanged }) {
  const editable = ['open', 'closed'].includes(summary.status);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [links, setLinks] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [newSupplier, setNewSupplier] = useState(null);

  const load = useCallback(() => api.invitations(eventId)
    .then((r) => { setData(r); return r; })
    .catch((e) => { setError(e.message); return null; }), [api, eventId]);
  // summary.revision: a change made elsewhere reloads the list too.
  useEffect(() => { load(); }, [load, summary.revision]);

  const act = async (action, input) => {
    setBusy(true);
    setError('');
    try {
      const r = await api.invitationAction(eventId, action, input, summary.revision);
      // The only copy of a new link is in this response: show it before any
      // reload, so a failed reload can never lose it (Codex 2A round 1).
      if (r.links && r.links.length > 0) {
        const names = Object.fromEntries(((data && data.suppliers) || []).map((x) => [x.supplier_id, x.supplier_name]));
        if (r.result && r.result.supplier_name) names[r.result.supplier_id] = r.result.supplier_name;
        setLinks({ links: r.links, names });
      }
      try { await onChanged(); await load(); } catch (e) { /* the lists reload on the next change */ }
      return true;
    } catch (e) {
      if (e.code === 'stale_revision') {
        setError(STALE_MESSAGE);
        await onChanged();
        load();
      } else {
        setError(e.message);
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  const suppliers = (data && data.suppliers) || [];
  const active = suppliers.filter((s) => s.status !== 'removed');
  const removed = suppliers.filter((s) => s.status === 'removed');

  return (
    <div>
      {summary.status === 'open' && (
        <div className="ll-fbar">
          {!inviting && <button type="button" className="srce-btn" onClick={() => setInviting(true)}>Invite another supplier</button>}
          {inviting && (
            <div className="srce-builder">
              <span className="srce-label">Invite another supplier</span>
              <SupplierPicker api={api} value={newSupplier} onChange={setNewSupplier} />
              {newSupplier && (
                <ContactForm busy={busy} submitLabel="Invite" onCancel={() => { setInviting(false); setNewSupplier(null); }}
                  onSubmit={async (email, name) => {
                    if (await act('invite_supplier', { supplier_id: newSupplier.supplier_id, email, ...(name ? { name } : {}) })) {
                      setInviting(false);
                      setNewSupplier(null);
                    }
                  }} />
              )}
              {!newSupplier && <button type="button" className="ll-clr" onClick={() => setInviting(false)}>Cancel</button>}
            </div>
          )}
        </div>
      )}
      {error && <div className="srce-error" role="alert">{error}</div>}
      {links && (
        <div className="srce-panel">
          <InvitationLinks links={links.links} names={links.names} />
          <div className="srce-actions"><button type="button" className="ll-clr" onClick={() => setLinks(null)}>I have copied them</button></div>
        </div>
      )}
      {!data && !error && <div className="ll-loading">Loading invitations…</div>}
      {data && active.length === 0 && (
        <div className="ll-empty"><div className="ll-empty-t">No invited suppliers</div>
          {summary.status === 'open' ? 'Invite a supplier to ask for offers.' : 'No supplier is taking part.'}</div>
      )}
      {active.map((s) => <SupplierBlock key={s.supplier_id} s={s} editable={editable} busy={busy} act={act} />)}
      {removed.length > 0 && (
        <>
          <h2 className="srce-h2">Removed</h2>
          {removed.map((s) => <SupplierBlock key={s.supplier_id} s={s} editable={editable} busy={busy} act={act} />)}
        </>
      )}
    </div>
  );
}
